/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Hands the API client a way to get a fresh access token.
 *
 * Kept as a bridge so api/client.ts stays framework-free and testable — it
 * knows nothing about React or oidc-client-ts.
 */

import { useEffect } from "react";
import { useAuth } from "react-oidc-context";

import { setTokenProvider } from "../api/client.ts";

export function AuthBridge({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  useEffect(() => {
    setTokenProvider(async () => {
      const user = auth.user;
      if (!user) return null;
      // Renew slightly early rather than letting a request fail and retry —
      // one round trip saved on every expiry boundary.
      if (user.expires_in !== undefined && user.expires_in < 60) {
        try {
          const renewed = await auth.signinSilent();
          return renewed?.access_token ?? null;
        } catch {
          return user.access_token ?? null;
        }
      }
      return user.access_token ?? null;
    });
  }, [auth]);

  return <>{children}</>;
}
